import { useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useUpdateRecurringExpense } from '@/hooks/useRecurringExpenses';
import { useExpenseCategories } from '@/hooks/useAccounting';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Calendar } from '@/components/ui/calendar';
import { CalendarIcon } from 'lucide-react';
import { format } from 'date-fns';
import { fr, arSA } from 'date-fns/locale';
import { cn } from '@/lib/utils';
import type { RecurringExpense } from '@/types/entities';

interface EditRecurringExpenseDialogProps {
  restaurantId: string;
  recurringExpense: RecurringExpense;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSuccess?: () => void;
}

export function EditRecurringExpenseDialog({
  restaurantId,
  recurringExpense,
  open,
  onOpenChange,
  onSuccess,
}: EditRecurringExpenseDialogProps) {
  const { t, i18n } = useTranslation();
  const { data: categories } = useExpenseCategories(restaurantId);
  const updateRecurringMutation = useUpdateRecurringExpense(restaurantId);

  const isSubmittingRef = useRef(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});

  const [formData, setFormData] = useState({
    categoryId: recurringExpense.categoryId,
    nameAr: recurringExpense.nameAr,
    nameFr: recurringExpense.nameFr,
    amount: recurringExpense.amount,
    startDate: new Date(recurringExpense.startDate),
    isActive: recurringExpense.isActive,
    notes: recurringExpense.notes || '',
  });

  const locale = i18n.language === 'ar' ? arSA : fr;
  const isRTL = i18n.language === 'ar';

  useEffect(() => {
    setFormData({
      categoryId: recurringExpense.categoryId,
      nameAr: recurringExpense.nameAr,
      nameFr: recurringExpense.nameFr,
      amount: recurringExpense.amount,
      startDate: new Date(recurringExpense.startDate),
      isActive: recurringExpense.isActive,
      notes: recurringExpense.notes || '',
    });
    setErrors({});
  }, [recurringExpense]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (isSubmittingRef.current) return;

    isSubmittingRef.current = true;
    setIsSubmitting(true);
    setErrors({});

    const newErrors: Record<string, string> = {};
    if (!formData.categoryId) {
      newErrors['categoryId'] = t('validation.required');
    }
    if (!formData.nameAr && !formData.nameFr) {
      newErrors['nameAr'] = t('validation.required');
      newErrors['nameFr'] = t('validation.required');
    }
    if (!formData.amount || formData.amount <= 0) {
      newErrors['amount'] = t('validation.required');
    }

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      isSubmittingRef.current = false;
      setIsSubmitting(false);
      return;
    }

    try {
      const payload: any = {
        recurringExpenseId: recurringExpense.id,
        categoryId: formData.categoryId,
        nameAr: formData.nameAr,
        nameFr: formData.nameFr,
        amount: formData.amount,
        startDate: format(formData.startDate, 'yyyy-MM-dd'),
        isActive: formData.isActive,
      };
      if (formData.notes) payload.notes = formData.notes;

      await updateRecurringMutation.mutateAsync(payload);
      onOpenChange(false);
      onSuccess?.();
    } catch (error) {
      setErrors({ form: error instanceof Error ? error.message : t('error.generic') });
    } finally {
      isSubmittingRef.current = false;
      setIsSubmitting(false);
    }
  };

  const activeCategories = categories?.filter((c) => c.isActive && c.icon !== 'users') || [];

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="w-[95vw] max-w-[95vw] sm:w-[90vw] sm:max-w-[90vw] md:max-w-[500px] max-h-[90vh] overflow-y-auto">
        <form onSubmit={handleSubmit}>
          <DialogHeader>
            <DialogTitle>{t('accounting.editRecurringExpense')}</DialogTitle>
            <DialogDescription>{t('accounting.editRecurringExpenseDescription')}</DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            {/* Category */}
            <div className="space-y-2">
              <Label htmlFor="category">{t('accounting.category')} *</Label>
              <Select
                value={formData.categoryId}
                onValueChange={(value) => setFormData({ ...formData, categoryId: value })}
              >
                <SelectTrigger className={errors['categoryId'] ? 'border-destructive' : ''}>
                  <SelectValue placeholder={t('accounting.selectCategory')} />
                </SelectTrigger>
                <SelectContent>
                  {activeCategories.map((category) => (
                    <SelectItem key={category.id} value={category.id}>
                      <span style={{ color: category.color }}>
                        {i18n.language === 'ar' ? category.nameAr : category.nameFr}
                      </span>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {errors['categoryId'] && (
                <p className="text-xs text-destructive">{errors['categoryId']}</p>
              )}
            </div>

            {/* Name AR */}
            <div className="space-y-2">
              <Label htmlFor="nameAr">{t('accounting.name')} (AR) *</Label>
              <Input
                id="nameAr"
                value={formData.nameAr}
                onChange={(e) => setFormData({ ...formData, nameAr: e.target.value })}
                className={errors['nameAr'] ? 'border-destructive' : ''}
                dir="rtl"
              />
              {errors['nameAr'] && <p className="text-xs text-destructive">{errors['nameAr']}</p>}
            </div>

            {/* Name FR */}
            <div className="space-y-2">
              <Label htmlFor="nameFr">{t('accounting.name')} (FR) *</Label>
              <Input
                id="nameFr"
                value={formData.nameFr}
                onChange={(e) => setFormData({ ...formData, nameFr: e.target.value })}
                className={errors['nameFr'] ? 'border-destructive' : ''}
              />
              {errors['nameFr'] && <p className="text-xs text-destructive">{errors['nameFr']}</p>}
            </div>

            {/* Monthly Amount */}
            <div className="space-y-2">
              <Label htmlFor="amount">
                {t('accounting.monthlyAmount')} ({t('common.currency')}) *
              </Label>
              <Input
                id="amount"
                type="number"
                step="0.01"
                min="0.01"
                value={formData.amount === 0 ? '' : formData.amount}
                onChange={(e) => {
                  const value = e.target.value === '' ? 0 : parseFloat(e.target.value);
                  setFormData({ ...formData, amount: isNaN(value) ? 0 : value });
                }}
                placeholder="0.00"
                className={errors['amount'] ? 'border-destructive' : ''}
              />
              {errors['amount'] && <p className="text-xs text-destructive">{errors['amount']}</p>}
            </div>

            {/* Start Date */}
            <div className="space-y-2">
              <Label>{t('accounting.startDate')} *</Label>
              <Popover>
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    className={cn(
                      'w-full justify-start text-left font-normal',
                      !formData.startDate && 'text-muted-foreground'
                    )}
                  >
                    <CalendarIcon className={cn('h-4 w-4', isRTL ? 'ml-2' : 'mr-2')} />
                    {formData.startDate ? (
                      format(formData.startDate, 'PPP', { locale })
                    ) : (
                      <span>{t('common.selectDate')}</span>
                    )}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0" align="start">
                  <Calendar
                    mode="single"
                    selected={formData.startDate}
                    onSelect={(date) => date && setFormData({ ...formData, startDate: date })}
                    initialFocus
                    locale={locale}
                    dir={isRTL ? 'rtl' : 'ltr'}
                  />
                </PopoverContent>
              </Popover>
              <p className="text-xs text-muted-foreground">
                {t('accounting.recurringStartDateHelp')}
              </p>
            </div>

            {/* Active Status */}
            <div className="flex items-center justify-between py-2">
              <Label htmlFor="isActive">{t('accounting.activeRecurring')}</Label>
              <Switch
                id="isActive"
                checked={formData.isActive}
                onCheckedChange={(checked) => setFormData({ ...formData, isActive: checked })}
              />
            </div>

            {/* Notes */}
            <div className="space-y-2">
              <Label htmlFor="notes">{t('accounting.notes')}</Label>
              <Textarea
                id="notes"
                value={formData.notes}
                onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                rows={2}
              />
            </div>

            {errors['form'] && (
              <div className="p-3 text-sm text-destructive bg-destructive/10 rounded-md">
                {errors['form']}
              </div>
            )}
          </div>

          <DialogFooter className="flex-col sm:flex-row gap-2 sm:gap-0">
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              disabled={isSubmitting}
            >
              {t('common.cancel')}
            </Button>
            <Button type="submit" disabled={isSubmitting}>
              {isSubmitting ? t('common.loading') : t('common.save')}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
